import { FormEventHandler, useMemo } from 'react';

import { useEventHandler } from '../../../../../hooks/useEventHandler';
import { Nullable } from '../../../../../types';
import { AccordionRadioGroupInputProps } from '../../types/AccordionRadioGroupsInputProps';
import { getSelectedGroup } from './logic/getSelectedGroup';
import { getSelectedOptionFromGroupOptions } from './logic/getSelectedOptionFromGroupOptions';

export const useAccordionRadioGroups = ({
  groups,
  value,
  onChange,
  hooks,
  name,
}: {
  groups: AccordionRadioGroupInputProps['groups'];
  value: AccordionRadioGroupInputProps['value'];
  onChange: AccordionRadioGroupInputProps['onChange'];
  hooks: AccordionRadioGroupInputProps['hooks'];
  name: AccordionRadioGroupInputProps['name'];
}) => {
  const selectedGroup = useMemo(
    () => getSelectedGroup(groups, value),
    [groups, value],
  );

  const selectedOption = useMemo(
    () => getSelectedOptionFromGroupOptions(value, selectedGroup?.options),
    [selectedGroup, value],
  );

  const onClearSelectedOption = useEventHandler(() => {
    onChange(null);
  });

  const onSelectedOptionChange = useEventHandler(
    (event: FormEventHandler<HTMLDivElement>) => {
      if (!('target' in event)) return;

      const selectedOptionValue: Nullable<string> = (
        event.target as HTMLInputElement
      ).value;
      onChange(selectedOptionValue);

      const newSelectedGroup = getSelectedGroup(groups, selectedOptionValue);
      const newSelectedOption = getSelectedOptionFromGroupOptions(
        selectedOptionValue,
        newSelectedGroup?.options,
      );

      hooks?.afterOnChange?.({
        name,
        value: newSelectedOption,
      });
    },
  );

  return {
    onSelectedOptionChange,
    selectedGroup,
    selectedOption,
    onClearSelectedOption,
  };
};
